import { useState } from 'react'
import styled from 'styled-components'
import { Button, ChevronLeftIcon } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { smallCaps } from '@farewill/ui/helpers/text'
import { screenMax, screenMin } from '@farewill/ui/helpers/responsive'

import { QUESTIONNAIRE_SECTIONS } from '../constants'
import useActiveSection from '../use-active-section'
import { Uppercase } from 'components/styled/uppercase'
// an instant jump, as a smooth scroll gets cut short when leaving a field saves it
import { scrollToDataId } from 'lib/window/scroll'

// the part of the nav that folds away: the section list and back to top
const FOLDING_ID = 'estate-questionnaire-section-nav-folding'
const SECTIONS = Object.values(QUESTIONNAIRE_SECTIONS)
// module level so useActiveSection doesn't re-observe the sections every render
const SECTION_IDS = SECTIONS.map(({ id }) => id)

// mirrors StyledButtonWrapper in components/tasks-panel/index.js
const StyledHeader = styled.div<{ $isFolded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${GTR.S};
  padding-bottom: ${GTR.S};
  margin-bottom: ${GTR.M};
  border-bottom: 1px solid ${COLOR.GREY.LIGHT};

  // folded, the header is all that shows, so it needs no divider
  ${({ $isFolded }) =>
    $isFolded &&
    screenMax.l`
      padding-bottom: 0;
      margin-bottom: 0;
      border-bottom: 0;
    `}
`

const StyledHeading = styled(Uppercase)`
  margin: 0;
`

// mirrors StyledExpandButton in components/tasks-panel/index.js
const StyledFoldButton = styled(Button)`
  ${smallCaps}

  color: ${COLOR.GREY.MEDIUM};
  margin: 0;

  // folding only applies when the nav overlays the form
  ${screenMin.xl`
    display: none;
  `}
`

// mirrors StyledIconChevron in components/tasks-panel/index.js
const StyledChevron = styled(ChevronLeftIcon)<{ $rotate: boolean }>`
  margin-right: ${GTR.XS};

  ${({ $rotate }) => $rotate && `transform: rotate(-180deg);`}
`

const StyledFolding = styled.div<{ $isFolded: boolean }>`
  ${({ $isFolded }) =>
    $isFolded &&
    screenMax.l`
      display: none;
    `}
`

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${GTR.XS};
  margin: 0;
  padding: 0;
  list-style: none;
`

const sharedLinkStyle = `
text-decoration:none;
&:hover { text-decoration: underline; }
`

// mirrors StyledNavLink's active state in components/tabs/index.tsx
const StyledSectionButton = styled(Button.Underline)<{ $isCurrent: boolean }>`
  ${({ $isCurrent }) =>
    $isCurrent
      ? `font-weight: ${FONT.WEIGHT.BOLD};` + sharedLinkStyle
      : `font-weight: ${FONT.WEIGHT.REGULAR};` + sharedLinkStyle}
`

const StyledBackToTopButton = styled(Button.Underline)`
  margin-top: ${GTR.M};
  font-weight: ${FONT.WEIGHT.REGULAR};
`

const SectionNav = () => {
  // starts folded so it doesn't cover the form on narrow screens
  const [isFolded, setIsFolded] = useState(true)
  const { activeId, setActiveId } = useActiveSection(SECTION_IDS)

  return (
    <nav aria-label="Estate questionnaire sections">
      <StyledHeader $isFolded={isFolded}>
        <StyledHeading color={COLOR.GREY.MEDIUM}>Sections</StyledHeading>
        <StyledFoldButton
          type="button"
          aria-expanded={!isFolded}
          aria-controls={FOLDING_ID}
          aria-label={isFolded ? 'Show sections' : 'Hide sections'}
          onClick={() => setIsFolded(!isFolded)}
        >
          <StyledChevron size="S" $rotate={!isFolded} />
          {isFolded ? 'Show' : 'Hide'}
        </StyledFoldButton>
      </StyledHeader>
      <StyledFolding id={FOLDING_ID} $isFolded={isFolded}>
        <StyledList>
          {SECTIONS.map(({ id, heading }) => (
            <li key={id}>
              <StyledSectionButton
                flush
                type="button"
                textAlignOnWrap="left"
                aria-current={id === activeId ? 'location' : undefined}
                $isCurrent={id === activeId}
                onClick={() => {
                  // the last section can be too short to reach the top of the
                  // screen, so don't wait for the scroll to mark it current
                  setActiveId(id)
                  scrollToDataId(id)
                }}
              >
                {heading}
              </StyledSectionButton>
            </li>
          ))}
        </StyledList>
        <StyledBackToTopButton
          flush
          type="button"
          onClick={() => {
            // the case header can keep every section out of the band at the
            // very top, so don't wait for the scroll to mark the first current
            setActiveId(SECTION_IDS[0])
            // an instant jump, for the same reason as scrollToDataId
            window.scrollTo(0, 0)
          }}
        >
          Back to top
        </StyledBackToTopButton>
      </StyledFolding>
    </nav>
  )
}

export default SectionNav
